import React, { useRef, useState } from "react";
import { FiWifi } from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward, IoLogoNoSmoking } from "react-icons/io";
import { TbAirConditioning } from "react-icons/tb";
import { Navigate, useLocation } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import Footer from "../../components/footer/Footer";
import Header from "../../components/header/Header";
import HeaderToggle from "../../components/headerToggle/HeaderToggle";
import useWindowSize from "../../hooks/useWindowSize";
import "./RoomDetailScreen.css";

function RoomDetailScreen() {
  const { width: screenWidth } = useWindowSize();
  const location = useLocation();
  const room = location.state?.room;
  
  const sliderRef = useRef(null);
  const modalSliderRef = useRef(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);

  // Room verisi yoksa odalar sayfasına yönlendir
  if (!room) {
    return <Navigate to="/odalar" replace />;
  }

  const settings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  const handleNext = () => {
    sliderRef.current?.slickNext();
    modalSliderRef.current?.slickNext();
  };

  const handlePrev = () => {
    sliderRef.current?.slickPrev();
    modalSliderRef.current?.slickPrev();
  };

  const openModal = (index) => {
    setSelectedImage(index);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const goToSlide = (index) => {
    sliderRef.current?.slickGoTo(index);
  };

  // Modal açıkken ESC tuşu ile kapat
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      closeModal();
    }
  };

  return (
    <div className="App">
      {/* Header */}
      {screenWidth < 910 ? (
        <HeaderToggle />
      ) : (
        <Header />
      )}

      {/* Hero Image */}
      {screenWidth > 910 ? (
        <img
          src={room.image[1]}
          className="room-header-detail-image"
          alt={room.title}
        />
      ) : (
        <div className="room-header-min-screen" />
      )}

      {/* Room Title */}
      <section className="room-detail-header-row">
        <h1 className="room-detail-title">{room.title}</h1>
        <p className="room-detail-subtitle">{room.roomType}</p>
      </section>

      {/* Image Slider */}
      <section className="room-detail-info">
        {screenWidth > 519 && (
          <button 
            className="room-detail-nav-button" 
            onClick={handlePrev}
            aria-label="Önceki resim"
          >
            <IoIosArrowBack fontSize={screenWidth < 909 ? 47 : 58} />
          </button>
        )}

        <div className="room-detail-slider">
          <Slider ref={sliderRef} {...settings} arrows={false}>
            {room.image.map((imageUrl, index) => (
              <div key={index} onClick={() => openModal(index)}>
                <img
                  src={imageUrl}
                  alt={`${room.title} - Görsel ${index + 1}`}
                  className="room-detail-image"
                />
              </div>
            ))}
          </Slider>
        </div>

        {screenWidth > 519 && (
          <button 
            className="room-detail-nav-button" 
            onClick={handleNext}
            aria-label="Sonraki resim"
          >
            <IoIosArrowForward fontSize={screenWidth < 909 ? 47 : 58} />
          </button>
        )}
      </section>

      {/* Thumbnail Strip */}
      <div className="room-detail-images-slider">
        {room.image.map((imageUrl, index) => (
          <button 
            key={index} 
            onClick={() => goToSlide(index)}
            className="thumbnail-button"
          >
            <img
              src={imageUrl}
              alt={`Küçük resim ${index + 1}`}
              className="room-detail-images-slider-image"
            />
          </button>
        ))}
      </div>

      {/* Room Details */}
      <section className="room-detail-content">
        <p className="room-detail-explanation">{room.explanationRoom}</p>
        <p className="room-detail-price">{room.priceRange}</p>
        <p className="room-detail-price-warning">*Fiyatlarımız değişiklik gösterebilir</p>

        {/* Features */}
        <ul className="room-detail-features">
          {room.features.map((feature, index) => (
            <li key={index} className="room-detail-feature-item">
              • {feature}
            </li>
          ))}
        </ul>
      </section>

      {/* Amenities */}
      <section className="amenities-section">
        <div className="icon-container">
          <div className="room-icon-row">
            <FiWifi fontSize={60} color="var(--color-primary, #e01616)" />
            <h3 className="icon-title">Wi-Fi</h3>
          </div>
          <div className="room-icon-row">
            <IoLogoNoSmoking fontSize={60} color="var(--color-primary, #e01616)" />
            <h3 className="icon-title">Sigara İçilmez</h3>
          </div>
          <div className="room-icon-row">
            <TbAirConditioning fontSize={60} color="var(--color-primary, #e01616)" />
            <h3 className="icon-title">Klima</h3>
          </div>
        </div>
      </section>

      {/* Modal */}
      {modalOpen && (
        <div 
          className="room-detail-modal-overlay" 
          onClick={closeModal}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-modal="true"
        >
          <div className="room-detail-modal" onClick={(e) => e.stopPropagation()}>
            {screenWidth > 519 && (
              <button 
                className="room-detail-modal-nav" 
                onClick={handlePrev}
                aria-label="Önceki resim"
              >
                <IoIosArrowBack fontSize={screenWidth < 909 ? 47 : 58} />
              </button>
            )}

            <button 
              className="room-detail-modal-close" 
              onClick={closeModal}
              aria-label="Kapat"
            >
              &times;
            </button>

            <div className="room-detail-modal-content">
              <Slider
                ref={modalSliderRef}
                {...settings}
                arrows={false}
                initialSlide={selectedImage}
              >
                {room.image.map((imageUrl, index) => (
                  <div key={index}>
                    <img
                      src={imageUrl}
                      alt={`${room.title} - Görsel ${index + 1}`}
                      className="room-detail-modal-image"
                    />
                  </div>
                ))}
              </Slider>
            </div>

            {screenWidth > 519 && (
              <button 
                className="room-detail-modal-nav" 
                onClick={handleNext}
                aria-label="Sonraki resim"
              >
                <IoIosArrowForward fontSize={screenWidth < 909 ? 47 : 58} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}

export default RoomDetailScreen;
